import React, { useEffect, useState } from 'react';
import { View, Text, Button } from "../../../Components/Themed";
import { ActivityIndicator, TouchableOpacity, FlatList, TextInput, ScrollView } from 'react-native';
import { useColors } from '../../../constants/useColors';
import { BetRouterApi } from '../../api';
import { Icons, SearchBox } from '../../../Components';
import Pagination from '../../../Components/Pagination';
import Switch from '../../../Components/Switch';
import moment from 'moment-mini';
import type {
    RouterLeagueProps,
    RouterContestProps,
    PartnerContestProps,
    RawContestProps,
    RouterPartnerProps,
    RouterMarketProps,
    MarketContestProps,
    PartnerMarketProps,
    RawMarketProps,
    RouterMarketSideProps,
    RouterMarketVariableProps,
    RouterParticipantProps
} from '../../types';

type LeagueContestsProps = {
    league: RouterLeagueProps;
    initialContestId?: string; // Optional contest ID to auto-select
};

type LinkStatus = 'all' | 'complete' | 'partial' | 'none';

type ContestData = {
    contests: RouterContestProps[];
    partnerContests: PartnerContestProps[];
    partners: RouterPartnerProps[];
    participants: RouterParticipantProps[];
    selectedContest: RouterContestProps | null;
    loading: boolean;
    loadingMarkets: boolean;
    loadingModal: boolean;
    searchValue: string;
    currentTab: 'partners' | 'markets';
    linkStatusFilter: LinkStatus;
    dateFilter: 'all' | 'upcoming' | 'today' | 'past';
    customDateFrom: string;
    customDateTo: string;
    // Link modal state
    showLinkModal: boolean;
    selectedPartner: RouterPartnerProps | null;
    availableRawContests: RawContestProps[];
    selectedRawIds: string[];
    rawSearchValue: string;
    rawPage: number;
    // Auto-link modal state
    showAutoLinkModal: boolean;
    autoLinkResults: any;
    showAutoLinkResults: boolean;
    // Market auto-link modal state
    showMarketAutoLinkModal: boolean;
    allRouterMarkets: RouterMarketProps[];
    selectedMarketIds: string[];
    // Manage markets modal state
    showManageMarketsModal: boolean;
    marketContests: MarketContestProps[];
    // Partner markets state
    partnerMarkets: PartnerMarketProps[];
    // Link markets modal state
    showLinkMarketsModal: boolean;
    selectedMarketContest: MarketContestProps | null;
    availableRawMarkets: RawMarketProps[];
    alreadyLinkedRawMarkets: RawMarketProps[];
    // Add variable modal state
    showAddVariableModal: boolean;
    newVariableValue: string;
    // Inspect partner markets modal state
    showInspectModal: boolean;
    inspectPartnerMarkets: PartnerMarketProps[];
    // Lookup data for enriching displays
    marketSides: RouterMarketSideProps[];
    marketVariables: RouterMarketVariableProps[];
    // Manual link modal state
    showManualLinkModal: boolean;
    selectedRawMarket: RawMarketProps | null;
    linkingData: {
        market_sides: RouterMarketSideProps[];
        timeframes: any[];
        participants: Array<{
            partner_participant_id: string;
            router_participant_id: string;
            name: string;
            participant_type: string;
            external_id: string;
            raw_name: string;
            tags: string[];
        }>;
    } | null;
    manualLinkForm: {
        sideParticipants: Array<{
            market_side_id: string;
            partner_participant_id: string;
            price_key: string;
        }>;
        timeframe_id: string;
        variable: string;
        variable_type: 'total' | 'spread';
    };
    participantSearchValue: string;
    participantPage: number;
    selectedSideIndex: number;
};

const RAW_CONTESTS_PER_PAGE = 10;
const PARTICIPANTS_PER_PAGE = 10;

const LeagueContests = ({ league, initialContestId }: LeagueContestsProps) => {
    const Colors = useColors();

    const [contestData, setContestData] = useState<ContestData>({
        contests: [],
        partnerContests: [],
        partners: [],
        participants: [],
        selectedContest: null,
        loading: true,
        loadingMarkets: false,
        loadingModal: false,
        searchValue: '',
        currentTab: 'partners',
        linkStatusFilter: 'all',
        dateFilter: 'all',
        customDateFrom: '',
        customDateTo: '',
        showLinkModal: false,
        selectedPartner: null,
        availableRawContests: [],
        selectedRawIds: [],
        rawSearchValue: '',
        rawPage: 0,
        showAutoLinkModal: false,
        autoLinkResults: null,
        showAutoLinkResults: false,
        showMarketAutoLinkModal: false,
        allRouterMarkets: [],
        selectedMarketIds: [],
        showManageMarketsModal: false,
        marketContests: [],
        partnerMarkets: [],
        showLinkMarketsModal: false,
        selectedMarketContest: null,
        availableRawMarkets: [],
        alreadyLinkedRawMarkets: [],
        showAddVariableModal: false,
        newVariableValue: '',
        showInspectModal: false,
        inspectPartnerMarkets: [],
        marketSides: [],
        marketVariables: [],
        showManualLinkModal: false,
        selectedRawMarket: null,
        linkingData: null,
        manualLinkForm: {
            sideParticipants: [],
            timeframe_id: '',
            variable: '',
            variable_type: 'spread'
        },
        participantSearchValue: '',
        participantPage: 0,
        selectedSideIndex: 0
    });

    useEffect(() => {
        loadContestData();
    }, [league.router_league_id]);

    // Auto-select contest when data loads
    useEffect(() => {
        if (initialContestId && contestData.contests.length > 0 && !contestData.selectedContest) {
            const contest = contestData.contests.find(c => c.router_contest_id === initialContestId);
            if (contest) {
                console.log('Auto-selecting contest:', contest);
                setContestData(prev => ({ ...prev, selectedContest: contest }));
            }
        }
    }, [initialContestId, contestData.contests]);

    const loadContestData = async () => {
        setContestData(prev => ({ ...prev, loading: true }));
        try {
            const [contestsRes, partnerContestsRes, partnersRes, participantsRes, sidesRes, variablesRes] = await Promise.all([
                BetRouterApi.BetRouter.ContestApi.getOpenContests(league.router_league_id, 'open'),
                BetRouterApi.BetRouter.ContestApi.getPartnerContests(),
                BetRouterApi.BetRouter.PartnerApi.getPartners(),
                BetRouterApi.BetRouter.ParticipantApi.getRouterParticipants(league.router_league_id),
                // Load market sides and variables once - they're small and global
                BetRouterApi.BetRouter.MarketApi.getMarketSides(),
                BetRouterApi.BetRouter.MarketApi.getMarketVariables()
            ]);

            setContestData(prev => ({
                ...prev,
                contests: contestsRes.router_contests || [],
                partnerContests: Array.isArray(partnerContestsRes) ? partnerContestsRes : [],
                partners: Array.isArray(partnersRes) ? partnersRes : [],
                participants: Array.isArray(participantsRes) ? participantsRes : [],
                marketSides: Array.isArray(sidesRes) ? sidesRes : [],
                marketVariables: Array.isArray(variablesRes) ? variablesRes : [],
                loading: false
            }));
        } catch (error) {
            console.error('Failed to load contest data:', error);
            setContestData(prev => ({ ...prev, loading: false }));
        }
    };

    const loadRawContests = async (partner: RouterPartnerProps) => {
        try {
            setContestData(prev => ({ ...prev, loading: true }));
            const result = await BetRouterApi.BetRouter.ContestApi.loadRawContests(
                partner.partner_id,
                league.router_league_id
            );
            alert(`Loaded ${result.count} raw contests from ${partner.name}`);
            await loadContestData(); // Reload to get updated data
        } catch (error) {
            console.error('Error loading raw contests:', error);
            alert('Error loading raw contests: ' + (error as any).message);
        } finally {
            setContestData(prev => ({ ...prev, loading: false }));
        }
    };

    const openLinkModal = async (contest: RouterContestProps, partner: RouterPartnerProps) => {
        try {
            setContestData(prev => ({ ...prev, loading: true }));
            const data = await BetRouterApi.BetRouter.ContestApi.getAvailableRawContests(
                contest.router_contest_id,
                partner.partner_id
            );

            setContestData(prev => ({
                ...prev,
                availableRawContests: data.raw_contests || [],
                showLinkModal: true,
                selectedPartner: partner,
                selectedContest: contest,
                selectedRawIds: [],
                rawSearchValue: '',
                rawPage: 0,
                loading: false
            }));
        } catch (error) {
            console.error('Error loading raw contests:', error);
            setContestData(prev => ({ ...prev, loading: false }));
        }
    };

    const linkSelectedContests = async () => {
        if (!contestData.selectedContest || contestData.selectedRawIds.length === 0) return;

        try {
            setContestData(prev => ({ ...prev, loading: true }));
            await BetRouterApi.BetRouter.ContestApi.linkContest(
                contestData.selectedRawIds,
                contestData.selectedContest.router_contest_id
            );
            alert(`Linked ${contestData.selectedRawIds.length} raw contests`);
            setContestData(prev => ({ ...prev, showLinkModal: false }));
            await loadContestData(); // Reload to get updated data
        } catch (error) {
            console.error('Error linking contests:', error);
            alert('Error linking contests: ' + (error as any).message);
        } finally {
            setContestData(prev => ({ ...prev, loading: false }));
        }
    };

    const toggleRawContestSelection = (rawContestId: string) => {
        setContestData(prev => ({
            ...prev,
            selectedRawIds: prev.selectedRawIds.includes(rawContestId)
                ? prev.selectedRawIds.filter(id => id !== rawContestId)
                : [...prev.selectedRawIds, rawContestId]
        }));
    };

    const getUniquePartnerCountForContest = (contestId: string) => {
        const contestPartners = contestData.partnerContests.filter(pc => pc.router_contest_id === contestId);
        const uniquePartnerIds = new Set(contestPartners.map(pc => pc.partner_id));
        return uniquePartnerIds.size;
    };

    const isPartnerLinked = (contestId: string, partnerId: string) => {
        return contestData.partnerContests.some(
            pc => pc.router_contest_id === contestId && pc.partner_id === partnerId
        );
    };

    // Calculate link status for a contest
    const getContestLinkStatus = (contest: RouterContestProps): LinkStatus => {
        const linkedPartnerCount = getUniquePartnerCountForContest(contest.router_contest_id);
        const totalPartners = contestData.partners.length;

        if (linkedPartnerCount === 0) return 'none';
        if (linkedPartnerCount === totalPartners) return 'complete';
        return 'partial';
    };

    // Get contests by link status
    const getContestsByStatus = (status: LinkStatus) => {
        if (status === 'all') return contestData.contests;
        return contestData.contests.filter(contest => getContestLinkStatus(contest) === status);
    };

    const getLinkedExternalIds = (contestId: string, partnerId: string) => {
        return contestData.partnerContests
            .filter(pc => pc.router_contest_id === contestId && pc.partner_id === partnerId)
            .map(pc => pc.external_id);
    };

    const openManageMarketsModal = async (contest: RouterContestProps) => {
        try {
            setContestData(prev => ({ ...prev, loadingModal: true }));
            const data = await BetRouterApi.BetRouter.MarketApi.getAvailableToSupport(contest.router_contest_id);
            setContestData(prev => ({
                ...prev,
                allRouterMarkets: data.all_router_markets || [],
                marketContests: data.market_contests || [],
                showManageMarketsModal: true,
                loadingModal: false
            }));
        } catch (error) {
            console.error('Error loading available markets:', error);
            alert('Error loading markets: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingModal: false }));
        }
    };

    const toggleMarketSupport = async (routerMarketId: string) => {
        if (!contestData.selectedContest) return;

        const isSupported = contestData.marketContests.some(mc => mc.router_market_id === routerMarketId);

        try {
            setContestData(prev => ({ ...prev, loadingModal: true }));

            if (isSupported) {
                // Remove it
                const marketContest = contestData.marketContests.find(mc => mc.router_market_id === routerMarketId);
                if (marketContest) {
                    await BetRouterApi.BetRouter.MarketApi.removeSupportedMarket(marketContest.market_contest_id);
                }
            } else {
                // Add it
                await BetRouterApi.BetRouter.MarketApi.addSupportedMarket(
                    contestData.selectedContest.router_contest_id,
                    routerMarketId
                );
            }

            // Reload the data
            const data = await BetRouterApi.BetRouter.MarketApi.getAvailableToSupport(contestData.selectedContest.router_contest_id);
            setContestData(prev => ({
                ...prev,
                marketContests: data.market_contests || [],
                loadingModal: false
            }));
        } catch (error) {
            console.error('Error toggling market support:', error);
            alert('Error updating market: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingModal: false }));
        }
    };

    const loadSupportedMarkets = async (contest: RouterContestProps) => {
        try {
            setContestData(prev => ({ ...prev, loadingMarkets: true }));
            const data = await BetRouterApi.BetRouter.MarketApi.getSupportedMarkets(contest.router_contest_id);

            // Also load all router markets so we can display market info
            const allMarketsData = await BetRouterApi.BetRouter.MarketApi.getAvailableToSupport(contest.router_contest_id);

            setContestData(prev => ({
                ...prev,
                marketContests: data.market_contests || [],
                allRouterMarkets: allMarketsData.all_router_markets || [],
                loadingMarkets: false
            }));
        } catch (error) {
            console.error('Error loading supported markets:', error);
            setContestData(prev => ({ ...prev, loadingMarkets: false }));
        }
    };

    const removeSupportedMarket = async (marketContestId: string, contest: RouterContestProps) => {
        try {
            setContestData(prev => ({ ...prev, loadingMarkets: true }));
            await BetRouterApi.BetRouter.MarketApi.removeSupportedMarket(marketContestId);
            await loadSupportedMarkets(contest);
        } catch (error) {
            console.error('Error removing supported market:', error);
            alert('Error removing market: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingMarkets: false }));
        }
    };

    const loadPartnerMarketsForContest = async (contest: RouterContestProps) => {
        try {
            // Use contest-scoped endpoint to get only data for this specific contest
            const contestData = await BetRouterApi.BetRouter.ContestApi.getContest(contest.router_contest_id);

            setContestData(prev => ({
                ...prev,
                partnerMarkets: contestData.partner_markets || [],
                // marketSides and marketVariables are global, load them once
                // We'll fetch these only when needed or load them globally once
            }));
        } catch (error) {
            console.error('Error loading partner markets for contest:', error);
        }
    };

    // Helper to get participant name from partner_participant_id
    // This now uses linkingData which is loaded on-demand in the manual link modal
    const getParticipantName = (partner_participant_id: string): string | null => {
        if (!contestData.linkingData?.participants) return null;

        const participant = contestData.linkingData.participants.find(
            p => p.partner_participant_id === partner_participant_id
        );
        return participant?.name || null;
    };

    const openManualLinkModal = async (rawMarket: RawMarketProps) => {
        if (!contestData.selectedContest || !contestData.selectedMarketContest || !contestData.selectedPartner) return;

        try {
            setContestData(prev => ({ ...prev, loadingModal: true }));

            const data = await BetRouterApi.BetRouter.MarketApi.getMarketLinkingData(
                contestData.selectedContest.router_contest_id,
                contestData.selectedMarketContest.router_market_id,
                contestData.selectedPartner.partner_id
            );

            const routerMarket = contestData.allRouterMarkets.find(
                m => m.router_market_id === contestData.selectedMarketContest?.router_market_id
            );

            // Set default values
            const defaultTimeframe = data.timeframes?.[0]?.timeframe_id || '';
            const defaultVariableType = routerMarket?.default_variable_type || 'spread';

            // Initialize sideParticipants with empty entries for each side
            const sideParticipants = data.market_sides.map(side => ({
                market_side_id: side.market_side_id,
                partner_participant_id: '',
                price_key: ''
            }));

            setContestData(prev => ({
                ...prev,
                showManualLinkModal: true,
                selectedRawMarket: rawMarket,
                linkingData: data,
                manualLinkForm: {
                    sideParticipants: sideParticipants,
                    timeframe_id: defaultTimeframe,
                    variable: '',
                    variable_type: defaultVariableType
                },
                participantSearchValue: '',
                participantPage: 0,
                selectedSideIndex: 0,
                loadingModal: false
            }));
        } catch (error) {
            console.error('Error loading linking data:', error);
            alert('Error loading linking data: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingModal: false }));
        }
    };

    const submitManualLink = async () => {
        if (!contestData.selectedRawMarket || !contestData.selectedMarketContest) return;

        const routerMarket = contestData.allRouterMarkets.find(
            m => m.router_market_id === contestData.selectedMarketContest?.router_market_id
        );

        // Validate required fields - at least one side must have a participant
        const sidesWithParticipants = contestData.manualLinkForm.sideParticipants.filter(sp => sp.partner_participant_id);
        if (sidesWithParticipants.length === 0) {
            alert('Please select at least one participant');
            return;
        }

        if (routerMarket?.variable_required && !contestData.manualLinkForm.variable) {
            alert('Please enter a variable value');
            return;
        }

        try {
            setContestData(prev => ({ ...prev, loadingModal: true }));

            const baseVariable = contestData.manualLinkForm.variable ? parseFloat(contestData.manualLinkForm.variable) : 0;
            const isSpread = contestData.manualLinkForm.variable_type === 'spread';

            // Create a link for each side that has a participant selected
            const linkPromises = sidesWithParticipants.map(sideParticipant => {
                // Find the side info to determine if we need to flip the variable
                const side = contestData.linkingData!.market_sides.find(s => s.market_side_id === sideParticipant.market_side_id);

                // For spreads, flip the sign for away side (home keeps the value as entered)
                let variableValue = baseVariable;
                if (isSpread && side?.side_key.toLowerCase() === 'away') {
                    variableValue = baseVariable * -1;
                }

                return BetRouterApi.BetRouter.MarketApi.linkMarket(
                    contestData.selectedRawMarket!.raw_market_id,
                    contestData.selectedMarketContest!.router_market_id,
                    sideParticipant.market_side_id,
                    sideParticipant.partner_participant_id,
                    contestData.manualLinkForm.timeframe_id,
                    variableValue,
                    contestData.manualLinkForm.variable_type,
                    sideParticipant.price_key || undefined
                );
            });

            await Promise.all(linkPromises);

            // Reload data
            if (contestData.selectedContest) {
                await Promise.all([
                    loadPartnerMarketsForContest(contestData.selectedContest),
                    openLinkMarketsModal(contestData.selectedMarketContest, contestData.selectedPartner!)
                ]);
            }

            setContestData(prev => ({
                ...prev,
                showManualLinkModal: false,
                selectedRawMarket: null,
                linkingData: null,
                loadingModal: false
            }));

            alert(`Successfully linked ${sidesWithParticipants.length} market side${sidesWithParticipants.length !== 1 ? 's' : ''}!`);
        } catch (error) {
            console.error('Error linking market:', error);
            alert('Error linking market: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingModal: false }));
        }
    };

    const openLinkMarketsModal = async (marketContest: MarketContestProps, partner: RouterPartnerProps) => {
        if (!contestData.selectedContest) return;

        try {
            setContestData(prev => ({ ...prev, loadingModal: true }));
            const data = await BetRouterApi.BetRouter.MarketApi.getAvailableRawMarkets(
                contestData.selectedContest.router_contest_id,
                marketContest.router_market_id,
                partner.partner_id
            );

            setContestData(prev => ({
                ...prev,
                availableRawMarkets: data.raw_markets || [],
                alreadyLinkedRawMarkets: data.already_linked || [],
                showLinkMarketsModal: true,
                selectedMarketContest: marketContest,
                selectedPartner: partner,
                loadingModal: false
            }));
        } catch (error) {
            console.error('Error loading raw markets:', error);
            alert('Error loading markets: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingModal: false }));
        }
    };

    const openAddVariableModal = (marketContest: MarketContestProps, partner: RouterPartnerProps) => {
        setContestData(prev => ({
            ...prev,
            showAddVariableModal: true,
            selectedMarketContest: marketContest,
            selectedPartner: partner,
            newVariableValue: ''
        }));
    };

    const addVariable = async () => {
        if (!contestData.selectedContest || !contestData.selectedMarketContest || !contestData.selectedPartner) return;

        const variableValue = parseFloat(contestData.newVariableValue);
        if (isNaN(variableValue)) {
            alert('Please enter a valid number');
            return;
        }

        try {
            setContestData(prev => ({ ...prev, loadingModal: true }));
            const result = await BetRouterApi.BetRouter.MarketApi.addVariable(
                contestData.selectedContest.router_contest_id,
                contestData.selectedMarketContest.router_market_id,
                contestData.selectedPartner.partner_id,
                variableValue
            );

            // Reload partner markets to show the new links
            if (contestData.selectedContest) {
                await loadPartnerMarketsForContest(contestData.selectedContest);
            }

            setContestData(prev => ({
                ...prev,
                showAddVariableModal: false,
                newVariableValue: '',
                loadingModal: false
            }));

            alert(`Successfully added variable ${variableValue}. Created ${result.count} partner market${result.count !== 1 ? 's' : ''}.`);
        } catch (error) {
            console.error('Error adding variable:', error);
            alert('Error adding variable: ' + (error as any).message);
            setContestData(prev => ({ ...prev, loadingModal: false }));
        }
    };

    const inspectPartnerMarkets = (marketContest: MarketContestProps, partner: RouterPartnerProps) => {
        if (!contestData.selectedContest) return;

        //const routerMarket = contestData.allRouterMarkets.find(m => m.router_market_id === marketContest.router_market_id);

        // Filter partner markets for this specific market and partner
        // No need to filter by router_contest_id - data is already scoped to this contest
        const relevantPartnerMarkets = contestData.partnerMarkets.filter(
            pm => pm.router_market_id === marketContest.router_market_id &&
                  pm.partner_id === partner.partner_id
        );

        setContestData(prev => ({
            ...prev,
            showInspectModal: true,
            inspectPartnerMarkets: relevantPartnerMarkets,
            selectedMarketContest: marketContest,
            selectedPartner: partner
        }));
    };

    // Apply search, link status, and date filters, then sort by scheduled_datetime
    const filteredContests = contestData.contests
        .filter(c => {
            // Search filter
            const matchesSearch = c.contest_label.toLowerCase().includes(contestData.searchValue.toLowerCase());
            // Link status filter
            const matchesStatus = contestData.linkStatusFilter === 'all' || getContestLinkStatus(c) === contestData.linkStatusFilter;

            // Date filter
            let matchesDate = true;
            if (contestData.dateFilter !== 'all') {
                const contestDate = moment(c.scheduled_datetime);
                const now = moment();
                const todayStart = moment().startOf('day');
                const todayEnd = moment().endOf('day');

                if (contestData.dateFilter === 'upcoming') {
                    matchesDate = contestDate.isAfter(now);
                } else if (contestData.dateFilter === 'today') {
                    matchesDate = contestDate.isBetween(todayStart, todayEnd, undefined, '[]');
                } else if (contestData.dateFilter === 'past') {
                    matchesDate = contestDate.isBefore(now);
                }
            }

            return matchesSearch && matchesStatus && matchesDate;
        })
        .sort((a, b) => {
            // Sort by scheduled_datetime ascending (earliest first)
            return moment(a.scheduled_datetime).valueOf() - moment(b.scheduled_datetime).valueOf();
        });

    const filteredRawContests = contestData.availableRawContests.filter(c =>
        c.pretty_title.toLowerCase().includes(contestData.rawSearchValue.toLowerCase())
    );

    const totalRawPages = Math.ceil(filteredRawContests.length / RAW_CONTESTS_PER_PAGE);
    const paginatedRawContests = filteredRawContests.slice(
        contestData.rawPage * RAW_CONTESTS_PER_PAGE,
        (contestData.rawPage + 1) * RAW_CONTESTS_PER_PAGE
    );

    if (contestData.loading) {
        return (
            <View transparent style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator size="large" color={Colors.text.h1} />
            </View>
        );
    }

    return (
        <>
            <View transparent style={{ flex: 1 }}>
                {/* Header */}
                <View transparent style={{ marginBottom: 24 }}>
                    <Text theme='h1' size={24}>Contests</Text>
                    <Text theme='description' size={14} style={{ marginTop: 8 }}>
                        {league.name} - Manage contests and partner mappings
                    </Text>
                </View>

            {/* Load Raw Contests Section */}
            <View type='body' style={{ padding: 16, marginBottom: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light }}>
                <View type='header' style={{ padding: 16, marginBottom: 12 }}>
                    <Text theme='h2' size={16}>Load Raw Contests from Partners</Text>
                    <Text theme='description' size={12} style={{ marginTop: 4 }}>
                        Fetch contests from each partner's API
                    </Text>
                </View>

                <View transparent style={{ paddingHorizontal: 16, paddingBottom: 16 }}>
                    {contestData.partners.map(partner => (
                        <View
                            key={partner.partner_id}
                            transparent
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                marginBottom: 8,
                                padding: 12,
                                backgroundColor: Colors.views.header,
                                borderRadius: 6
                            }}
                        >
                            <Text theme='h2' size={13} style={{ flex: 1 }}>{partner.name}</Text>
                            <Button
                                type='action'
                                title="Load Contests"
                                onPress={() => loadRawContests(partner)}
                                style={{ paddingHorizontal: 15, paddingVertical: 8 }}
                            />
                        </View>
                    ))}
                </View>
            </View>

            {/* Link Status Stats */}
            <View transparent style={{ flexDirection: 'row', gap: 12, marginBottom: 16 }}>
                {(() => {
                    const allCount = contestData.contests.length;
                    const completeCount = getContestsByStatus('complete').length;
                    const partialCount = getContestsByStatus('partial').length;
                    const noneCount = getContestsByStatus('none').length;

                    const stats = [
                        { status: 'all' as LinkStatus, label: 'All Contests', count: allCount, color: Colors.text.h1 },
                        { status: 'complete' as LinkStatus, label: 'Fully Linked', count: completeCount, color: Colors.text.success },
                        { status: 'partial' as LinkStatus, label: 'Partially Linked', count: partialCount, color: Colors.text.warning },
                        { status: 'none' as LinkStatus, label: 'Not Linked', count: noneCount, color: Colors.text.error },
                    ];

                    return stats.map(stat => (
                        <TouchableOpacity
                            key={stat.status}
                            onPress={() => setContestData(prev => ({ ...prev, linkStatusFilter: stat.status }))}
                            style={{
                                flex: 1,
                                padding: 16,
                                backgroundColor: Colors.views.header,
                                borderRadius: 8,
                                borderWidth: 2,
                                borderColor: contestData.linkStatusFilter === stat.status ? stat.color : Colors.borders.light,
                            }}
                        >
                            <Text theme='description' size={11} style={{ marginBottom: 4 }}>
                                {stat.label.toUpperCase()}
                            </Text>
                            <Text size={28} style={{ color: stat.color, fontWeight: '600' }}>
                                {stat.count}
                            </Text>
                        </TouchableOpacity>
                    ));
                })()}
            </View>

            {/* Contests List */}
            <View type='body' style={{ flex: 1, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light, overflow: 'hidden' }}>
                <View type='header' style={{ padding: 16 }}>
                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View transparent>
                            <Text theme='h2' size={16}>Router Contests</Text>
                            <Text theme='description' size={12} style={{ marginTop: 4 }}>
                                {filteredContests.length} contest{filteredContests.length !== 1 ? 's' : ''}
                                {contestData.linkStatusFilter !== 'all' && ` (${contestData.linkStatusFilter})`}
                            </Text>
                        </View>
                        <View transparent style={{ flexDirection: 'row', gap: 8 }}>
                            <Button
                                type='action'
                                title="🔗 Auto-Link Contests"
                                onPress={() => setContestData(prev => ({ ...prev, showAutoLinkModal: true }))}
                                style={{ paddingHorizontal: 15, paddingVertical: 8 }}
                            />
                            <Button
                                type='action'
                                title="🔗 Auto-Link Markets"
                                onPress={async () => {
                                    try {
                                        setContestData(prev => ({ ...prev, loading: true }));
                                        const markets = await BetRouterApi.BetRouter.MarketApi.getRouterMarkets();
                                        setContestData(prev => ({
                                            ...prev,
                                            allRouterMarkets: Array.isArray(markets) ? markets : [],
                                            showMarketAutoLinkModal: true,
                                            loading: false
                                        }));
                                    } catch (error) {
                                        console.error('Error loading router markets:', error);
                                        alert('Error loading markets: ' + (error as any).message);
                                        setContestData(prev => ({ ...prev, loading: false }));
                                    }
                                }}
                                style={{ paddingHorizontal: 15, paddingVertical: 8 }}
                            />
                        </View>
                    </View>

                    <View transparent style={{ marginTop: 12 }}>
                        <SearchBox
                            placeholder="Search contests..."
                            onChange={(text) => setContestData(prev => ({ ...prev, searchValue: text }))}
                            hide_search_button={true}
                        />
                    </View>

                    {/* Date Filter */}
                    <View transparent style={{ marginTop: 12 }}>
                        <Text theme='description' size={11} style={{ marginBottom: 6, textTransform: 'uppercase' }}>
                            Filter by Date
                        </Text>
                        <View transparent style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                            {[
                                { value: 'all', label: 'All' },
                                { value: 'today', label: 'Today' },
                                { value: 'upcoming', label: 'Upcoming' },
                                { value: 'past', label: 'Past' }
                            ].map(filter => (
                                <TouchableOpacity
                                    key={filter.value}
                                    onPress={() => setContestData(prev => ({ ...prev, dateFilter: filter.value as any }))}
                                    style={{
                                        paddingHorizontal: 14,
                                        paddingVertical: 7,
                                        borderRadius: 6,
                                        borderWidth: 1,
                                        borderColor: contestData.dateFilter === filter.value ? Colors.text.success : Colors.borders.light,
                                        backgroundColor: contestData.dateFilter === filter.value ? Colors.views.body : 'transparent'
                                    }}
                                >
                                    <Text
                                        theme={contestData.dateFilter === filter.value ? 'h2' : 'description'}
                                        size={12}
                                    >
                                        {filter.label}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                </View>

                <FlatList
                    data={filteredContests}
                    keyExtractor={(item) => item.router_contest_id}
                    renderItem={({ item: contest }) => {
                        const partnerCount = getUniquePartnerCountForContest(contest.router_contest_id);
                        const isExpanded = contestData.selectedContest?.router_contest_id === contest.router_contest_id;

                        return (
                            <View transparent style={{ borderBottomWidth: 1, borderBottomColor: Colors.borders.light }}>
                                {/* Contest Header */}
                                <TouchableOpacity
                                    onPress={() => {
                                        if (isExpanded) {
                                            setContestData(prev => ({ ...prev, selectedContest: null }));
                                        } else {
                                            setContestData(prev => ({ ...prev, selectedContest: contest }));
                                            // Load supported markets and partner markets when expanding
                                            loadSupportedMarkets(contest);
                                            loadPartnerMarketsForContest(contest);
                                        }
                                    }}
                                    style={{
                                        padding: 16,
                                        backgroundColor: isExpanded ? Colors.views.header : 'transparent',
                                        flexDirection: 'row',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Icons.ChevronIcon
                                        direction={isExpanded ? 'down' : 'right'}
                                        size={8}
                                        color={Colors.text.h1}
                                    />
                                    <View transparent style={{ flex: 1, marginLeft: 12 }}>
                                        <Text theme='h2' size={14}>{contest.contest_label}</Text>
                                        <Text theme='description' size={11} style={{ marginTop: 2 }}>
                                            {moment(contest.scheduled_datetime).format('ddd, MMM D, YYYY [at] h:mm A')} • {partnerCount} partner{partnerCount !== 1 ? 's' : ''}
                                        </Text>
                                    </View>
                                    <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Text theme='description' size={12}>Auto-fill:</Text>
                                        <Switch
                                            value={contest.auto_fill ?? false}
                                            switch_type='on_off'
                                            size={14}
                                            onChange={async (value) => {
                                                try {
                                                    await BetRouterApi.BetRouter.ContestApi.updateRouterContest(
                                                        contest.router_contest_id,
                                                        { auto_fill: value }
                                                    );
                                                    setContestData(prev => ({
                                                        ...prev,
                                                        contests: prev.contests.map(c =>
                                                            c.router_contest_id === contest.router_contest_id
                                                                ? { ...c, auto_fill: value }
                                                                : c
                                                        )
                                                    }));
                                                } catch (error) {
                                                    console.error('Failed to update auto_fill:', error);
                                                    alert('Failed to update auto-fill setting');
                                                }
                                            }}
                                        />
                                    </View>
                                </TouchableOpacity>

                                {/* Expanded Contest Details with Tabs */}
                                {isExpanded && (
                                    <View transparent style={{ paddingTop: 0 }}>
                                        {/* Tabs */}
                                        <View transparent style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: Colors.borders.light, paddingHorizontal: 16 }}>
                                            <TouchableOpacity
                                                onPress={() => setContestData(prev => ({ ...prev, currentTab: 'partners' }))}
                                                style={{
                                                    paddingVertical: 12,
                                                    paddingHorizontal: 16,
                                                    borderBottomWidth: contestData.currentTab === 'partners' ? 2 : 0,
                                                    borderBottomColor: Colors.text.action
                                                }}
                                            >
                                                <Text
                                                    theme={contestData.currentTab === 'partners' ? 'h2' : 'description'}
                                                    size={13}
                                                    style={{ color: contestData.currentTab === 'partners' ? Colors.text.action : Colors.text.description }}
                                                >
                                                    Partner Links
                                                </Text>
                                            </TouchableOpacity>
                                            <TouchableOpacity
                                                onPress={() => {
                                                    setContestData(prev => ({ ...prev, currentTab: 'markets' }));
                                                    loadSupportedMarkets(contest);
                                                    loadPartnerMarketsForContest(contest);
                                                }}
                                                style={{
                                                    paddingVertical: 12,
                                                    paddingHorizontal: 16,
                                                    borderBottomWidth: contestData.currentTab === 'markets' ? 2 : 0,
                                                    borderBottomColor: Colors.text.action
                                                }}
                                            >
                                                <Text
                                                    theme={contestData.currentTab === 'markets' ? 'h2' : 'description'}
                                                    size={13}
                                                    style={{ color: contestData.currentTab === 'markets' ? Colors.text.action : Colors.text.description }}
                                                >
                                                    Markets
                                                </Text>
                                            </TouchableOpacity>
                                        </View>

                                        {/* Tab Content */}
                                        <View transparent style={{ padding: 16 }}>
                                            {contestData.currentTab === 'partners' ? (
                                                // Partners tab
                                                <>
                                                    {contestData.partners.map(partner => {
                                                        const linked = isPartnerLinked(contest.router_contest_id, partner.partner_id);
                                                        const externalIds = getLinkedExternalIds(contest.router_contest_id, partner.partner_id);

                                                        return (
                                                            <View
                                                                key={partner.partner_id}
                                                                transparent
                                                                style={{
                                                                    padding: 12,
                                                                    marginBottom: 8,
                                                                    backgroundColor: Colors.views.header,
                                                                    borderRadius: 6,
                                                                    flexDirection: 'row',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <View transparent style={{ flex: 1 }}>
                                                                    <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                                        <Text theme='h2' size={13}>{partner.name}</Text>
                                                                        {linked ? (
                                                                            <Icons.CheckCirlceIcon color={Colors.text.success} size={14} />
                                                                        ) : (
                                                                            <Text theme='description' size={11}>Not Linked</Text>
                                                                        )}
                                                                    </View>
                                                                    {linked && externalIds.length > 0 && (
                                                                        <Text theme='description' size={11} style={{ marginTop: 4 }}>
                                                                            External IDs: {externalIds.join(', ')}
                                                                        </Text>
                                                                    )}
                                                                </View>
                                                                <Button
                                                                    type='action'
                                                                    title={linked ? 'Manage Links' : 'Link'}
                                                                    onPress={() => openLinkModal(contest, partner)}
                                                                    style={{ paddingHorizontal: 12, paddingVertical: 6 }}
                                                                />
                                                            </View>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                // Markets tab
                                                <>
                                                    <Button
                                                        type='action'
                                                        title='Manage Markets'
                                                        onPress={() => openManageMarketsModal(contest)}
                                                        style={{ marginBottom: 16 }}
                                                    />

                                                    {/* Loading indicator for markets */}
                                                    {contestData.loadingMarkets ? (
                                                        <View transparent style={{ padding: 40, alignItems: 'center' }}>
                                                            <ActivityIndicator size="small" color={Colors.text.h1} />
                                                            <Text theme='description' size={12} style={{ marginTop: 8 }}>Loading markets...</Text>
                                                        </View>
                                                    ) : contestData.marketContests.length === 0 ? (
                                                        <Text theme='description' size={12} style={{ textAlign: 'center', padding: 20, opacity: 0.6 }}>
                                                            No supported markets yet. Add a market above to get started.
                                                        </Text>
                                                    ) : (
                                                        contestData.marketContests.map(marketContest => {
                                                            const routerMarket = contestData.allRouterMarkets.find(rm => rm.router_market_id === marketContest.router_market_id);
                                                            if (!routerMarket) return null;

                                                            return (
                                                                <View
                                                                    key={marketContest.market_contest_id}
                                                                    transparent
                                                                    style={{
                                                                        marginBottom: 16,
                                                                        padding: 15,
                                                                        backgroundColor: Colors.views.header,
                                                                        borderRadius: 8,
                                                                        borderWidth: 1,
                                                                        borderColor: Colors.borders.light
                                                                    }}
                                                                >
                                                                    <View transparent style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                                                                        <Text theme='h2' size={14} style={{ flex: 1 }}>{routerMarket.market_label}</Text>
                                                                        <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginRight: 12 }}>
                                                                            <Text theme='description' size={11}>Auto-fill:</Text>
                                                                            <Switch
                                                                                value={marketContest.auto_fill ?? false}
                                                                                switch_type='on_off'
                                                                                size={12}
                                                                                onChange={async (value) => {
                                                                                    try {
                                                                                        await BetRouterApi.BetRouter.MarketApi.updateMarketContest(
                                                                                            marketContest.market_contest_id,
                                                                                            { auto_fill: value }
                                                                                        );
                                                                                        setContestData(prev => ({
                                                                                            ...prev,
                                                                                            marketContests: prev.marketContests.map(mc =>
                                                                                                mc.market_contest_id === marketContest.market_contest_id
                                                                                                    ? { ...mc, auto_fill: value }
                                                                                                    : mc
                                                                                            )
                                                                                        }));
                                                                                    } catch (error) {
                                                                                        console.error('Failed to update market auto_fill:', error);
                                                                                        alert('Failed to update auto-fill setting');
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </View>
                                                                        <TouchableOpacity onPress={() => removeSupportedMarket(marketContest.market_contest_id, contest)}>
                                                                            <Text theme='description' size={12} style={{ color: Colors.text.warning }}>Remove</Text>
                                                                        </TouchableOpacity>
                                                                    </View>

                                                                    <Text theme='description' size={11} style={{ marginBottom: 12 }}>
                                                                        {routerMarket.market_description}
                                                                    </Text>

                                                                    {/* Partner Market Links */}
                                                                    <View transparent style={{ paddingTop: 8, borderTopWidth: 1, borderTopColor: Colors.borders.light }}>
                                                                        {contestData.partners.map(partner => {
                                                                            // No need to filter by router_contest_id - data is already scoped to this contest
                                                                            const partnerMarketsForThis = contestData.partnerMarkets.filter(
                                                                                pm => pm.router_market_id === routerMarket.router_market_id &&
                                                                                      pm.partner_id === partner.partner_id
                                                                            );
                                                                            const isLinked = partnerMarketsForThis.length > 0;

                                                                            return (
                                                                                <View
                                                                                    key={partner.partner_id}
                                                                                    transparent
                                                                                    style={{
                                                                                        flexDirection: 'row',
                                                                                        alignItems: 'center',
                                                                                        padding: 10,
                                                                                        marginTop: 8
                                                                                    }}
                                                                                >
                                                                                    <View transparent style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                                                                        <Text theme='description' size={12}>{partner.name}:</Text>
                                                                                        {isLinked ? (
                                                                                            <>
                                                                                                <Icons.CheckCirlceIcon color={Colors.text.success} size={14} />
                                                                                                <Text theme='description' size={11}>
                                                                                                    ({partnerMarketsForThis.length} linked)
                                                                                                </Text>
                                                                                            </>
                                                                                        ) : (
                                                                                            <Text theme='description' size={11}>❌ Not Linked</Text>
                                                                                        )}
                                                                                    </View>
                                                                                    <View transparent style={{ flexDirection: 'row', gap: 8 }}>
                                                                                        {isLinked && (
                                                                                            <Button
                                                                                                type='close'
                                                                                                title='Inspect'
                                                                                                onPress={() => inspectPartnerMarkets(marketContest, partner)}
                                                                                                style={{ paddingHorizontal: 12, paddingVertical: 6 }}
                                                                                            />
                                                                                        )}
                                                                                        <Button
                                                                                            type='action'
                                                                                            title={!isLinked ? 'Link Markets' : routerMarket.variable_required ? '+ Add Variable' : '+ Add More'}
                                                                                            onPress={() => {
                                                                                                if (!isLinked || !routerMarket.variable_required) {
                                                                                                    openLinkMarketsModal(marketContest, partner);
                                                                                                } else {
                                                                                                    openAddVariableModal(marketContest, partner);
                                                                                                }
                                                                                            }}
                                                                                            style={{ paddingHorizontal: 12, paddingVertical: 6 }}
                                                                                        />
                                                                                    </View>
                                                                                </View>
                                                                            );
                                                                        })}
                                                                    </View>
                                                                </View>
                                                            );
                                                        })
                                                    )}
                                                </>
                                            )}
                                        </View>
                                    </View>
                                )}
                            </View>
                        );
                    }}
                />
            </View>

            {/* Link Modal */}
            {contestData.showLinkModal && (
                <View
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20
                    }}
                >
                    <View float style={{ width: '90%', maxWidth: 600, maxHeight: '80%', borderRadius: 8, overflow: 'hidden', flexDirection: 'column' }}>
                        <View type='header' style={{ padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <View transparent>
                                <Text theme='h1' size={18}>Link Raw Contests</Text>
                                <Text theme='description' size={12} style={{ marginTop: 4 }}>
                                    {contestData.selectedPartner?.name} → {contestData.selectedContest?.contest_label}
                                </Text>
                            </View>
                            <TouchableOpacity onPress={() => setContestData(prev => ({ ...prev, showLinkModal: false }))}>
                                <Icons.CloseIcon size={24} color={Colors.text.h1} />
                            </TouchableOpacity>
                        </View>

                        {/* Search in modal */}
                        <View transparent style={{ padding: 16, paddingTop: 0 }}>
                            <SearchBox
                                placeholder="Search raw contests..."
                                onChange={(text) => setContestData(prev => ({ ...prev, rawSearchValue: text, rawPage: 0 }))}
                                hide_search_button={true}
                            />
                        </View>

                        {/* Results count */}
                        <View transparent style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
                            <Text theme='description' size={12}>
                                Showing {paginatedRawContests.length} of {filteredRawContests.length} contest{filteredRawContests.length !== 1 ? 's' : ''}
                            </Text>
                        </View>

                        <FlatList
                            data={paginatedRawContests}
                            keyExtractor={(item) => item.raw_contest_id}
                            renderItem={({ item: rawContest }) => {
                                const isSelected = contestData.selectedRawIds.includes(rawContest.raw_contest_id);
                                return (
                                    <TouchableOpacity
                                        onPress={() => toggleRawContestSelection(rawContest.raw_contest_id)}
                                        style={{
                                            padding: 12,
                                            backgroundColor: isSelected ? Colors.views.header : Colors.views.body,
                                            borderBottomWidth: 1,
                                            borderBottomColor: Colors.borders.light,
                                            flexDirection: 'row',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <View transparent style={{ width: 24, height: 24, marginRight: 12 }}>
                                            {isSelected && <Icons.CheckIcon color={Colors.text.success} size={20} />}
                                        </View>
                                        <View transparent style={{ flex: 1 }}>
                                            <Text theme='h2' size={13}>{rawContest.pretty_title}</Text>
                                            <Text theme='description' size={11} style={{ marginTop: 2 }}>
                                                {new Date(rawContest.scheduled_datetime).toLocaleString()} • {rawContest.external_id}
                                            </Text>
                                        </View>
                                    </TouchableOpacity>
                                );
                            }}
                        />

                        {/* Pagination */}
                        {totalRawPages > 1 && (
                            <View transparent style={{ padding: 16, paddingTop: 8 }}>
                                <Pagination
                                    offset={contestData.rawPage}
                                    pages={totalRawPages}
                                    onNext={() => setContestData(prev => ({ ...prev, rawPage: prev.rawPage + 1 }))}
                                    onPrevious={() => setContestData(prev => ({ ...prev, rawPage: prev.rawPage - 1 }))}
                                />
                            </View>
                        )}

                        <View type='footer' style={{ padding: 16, flexDirection: 'row', justifyContent: 'flex-end', gap: 10 }}>
                            <Button
                                type='close'
                                title="Cancel"
                                onPress={() => setContestData(prev => ({ ...prev, showLinkModal: false }))}
                            />
                            <Button
                                type='action'
                                title={`Link ${contestData.selectedRawIds.length} Contest${contestData.selectedRawIds.length !== 1 ? 's' : ''}`}
                                onPress={linkSelectedContests}
                                disabled={contestData.selectedRawIds.length === 0}
                            />
                        </View>
                    </View>
                </View>
            )}
            </View>

            {/* Auto-Link Modal */}
            {contestData.showAutoLinkModal && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ maxWidth: 500, width: '100%', borderRadius: 8, overflow: 'hidden' }}>
                        <View type='header' style={{ padding: 20 }}>
                            <Text theme='h1' size={18}>Auto-Link Contests</Text>
                            <Text theme='description' size={14} style={{ marginTop: 5 }}>
                                Select partner to automatically match and link contests
                            </Text>
                        </View>

                        <View type='body' style={{ padding: 20 }}>
                            <Text theme='description' size={12} style={{ marginBottom: 15 }}>
                                This will attempt to automatically match contests based on scheduled time and participants.
                            </Text>

                            {contestData.partners.map(partner => {
                                const linkedCount = contestData.partnerContests.filter(pc => pc.partner_id === partner.partner_id).length;
                                const totalCount = contestData.contests.length;

                                return (
                                    <View
                                        key={partner.partner_id}
                                        transparent
                                        style={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            padding: 15,
                                            marginBottom: 10,
                                            backgroundColor: Colors.views.header,
                                            borderRadius: 8
                                        }}
                                    >
                                        <View transparent style={{ flex: 1 }}>
                                            <Text theme='h2' size={14}>{partner.name}</Text>
                                            <Text theme='description' size={11} style={{ marginTop: 3 }}>
                                                {linkedCount}/{totalCount} contests linked
                                            </Text>
                                        </View>
                                        <Button
                                            type='action'
                                            title='Auto-Link'
                                            onPress={async () => {
                                                try {
                                                    setContestData(prev => ({ ...prev, loading: true }));
                                                    const result = await BetRouterApi.BetRouter.AutoLinkingApi.autoLinkContests(
                                                        league.router_league_id,
                                                        partner.partner_id
                                                    );
                                                    setContestData(prev => ({
                                                        ...prev,
                                                        autoLinkResults: result,
                                                        showAutoLinkModal: false,
                                                        showAutoLinkResults: true
                                                    }));

                                                    // Reload partner contests
                                                    const allPartnerContests = await BetRouterApi.BetRouter.ContestApi.getPartnerContests();
                                                    setContestData(prev => ({
                                                        ...prev,
                                                        partnerContests: Array.isArray(allPartnerContests) ? allPartnerContests : [],
                                                        loading: false
                                                    }));
                                                } catch (error) {
                                                    console.error('Error auto-linking contests:', error);
                                                    alert('Error auto-linking contests: ' + (error as any).message);
                                                    setContestData(prev => ({ ...prev, loading: false }));
                                                }
                                            }}
                                            style={{ paddingHorizontal: 15, paddingVertical: 8 }}
                                        />
                                    </View>
                                );
                            })}
                        </View>

                        <View type='footer' style={{ padding: 15 }}>
                            <Button
                                type='close'
                                title='Cancel'
                                onPress={() => setContestData(prev => ({ ...prev, showAutoLinkModal: false }))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Auto-Link Results Modal */}
            {contestData.showAutoLinkResults && contestData.autoLinkResults && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ maxWidth: 600, width: '100%', maxHeight: '90%', borderRadius: 8, overflow: 'hidden' }}>
                        <View type='header' style={{ padding: 20 }}>
                            <Text theme='h1' size={18}>Auto-Link Results</Text>
                            <Text theme='description' size={14} style={{ marginTop: 5 }}>
                                {contestData.autoLinkResults.partner_name} • {league.name}
                            </Text>
                        </View>

                        <ScrollView style={{ flex: 1 }}>
                            <View transparent style={{ padding: 20 }}>
                                {/* Summary */}
                                <View type='body' style={{ padding: 16, borderRadius: 8, marginBottom: 16 }}>
                                    <Text theme='h2' size={16} style={{ marginBottom: 12 }}>Summary</Text>
                                    <Text theme='description' size={13} style={{ marginBottom: 6 }}>
                                        Successfully linked: {contestData.autoLinkResults.matched_count}/{contestData.autoLinkResults.total_router_entities} contests
                                    </Text>
                                    {contestData.autoLinkResults.failed_count > 0 && (
                                        <Text theme='description' size={13} style={{ color: Colors.text.warning }}>
                                            Failed: {contestData.autoLinkResults.failed_count} contests
                                        </Text>
                                    )}
                                </View>

                                {/* Failed Results */}
                                {contestData.autoLinkResults.results.filter((r: any) => !r.success).length > 0 && (
                                    <View type='body' style={{ padding: 16, borderRadius: 8 }}>
                                        <Text theme='h2' size={16} style={{ marginBottom: 12 }}>Failed Matches</Text>
                                        {contestData.autoLinkResults.results.filter((r: any) => !r.success).map((result: any, index: number) => (
                                            <View
                                                key={index}
                                                transparent
                                                style={{
                                                    padding: 12,
                                                    marginBottom: 8,
                                                    backgroundColor: Colors.views.header,
                                                    borderRadius: 6
                                                }}
                                            >
                                                <Text theme='h2' size={13}>{result.router_entity_label}</Text>
                                                <Text theme='description' size={11} style={{ marginTop: 4, color: Colors.text.warning }}>
                                                    {result.error || 'No matches found'}
                                                </Text>
                                            </View>
                                        ))}
                                    </View>
                                )}
                            </View>
                        </ScrollView>

                        <View type='footer' style={{ padding: 15 }}>
                            <Button
                                type='action'
                                title='Done'
                                onPress={() => setContestData(prev => ({
                                    ...prev,
                                    showAutoLinkResults: false,
                                    autoLinkResults: null
                                }))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Market Auto-Link Modal */}
            {contestData.showMarketAutoLinkModal && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ maxWidth: 700, width: '100%', maxHeight: '90%', borderRadius: 8, overflow: 'hidden' }}>
                        <View type='header' style={{ padding: 20 }}>
                            <Text theme='h1' size={18}>Auto-Link Markets</Text>
                            <Text theme='description' size={14} style={{ marginTop: 5 }}>
                                Select markets and partner to auto-link
                            </Text>
                        </View>

                        <ScrollView style={{ flex: 1 }}>
                            <View transparent style={{ padding: 20 }}>
                                {/* Market Selection */}
                                <Text theme='h2' size={14} style={{ marginBottom: 10 }}>
                                    Select Markets:
                                </Text>
                                <Text theme='description' size={12} style={{ marginBottom: 15 }}>
                                    Choose which markets to auto-link for the selected partner
                                </Text>

                                {contestData.allRouterMarkets.filter(m => m.status === 'active').map(market => {
                                    const isSelected = contestData.selectedMarketIds.includes(market.router_market_id);
                                    return (
                                        <TouchableOpacity
                                            key={market.router_market_id}
                                            style={{
                                                padding: 12,
                                                marginBottom: 8,
                                                backgroundColor: isSelected ? Colors.text.action : Colors.views.header,
                                                borderRadius: 6,
                                                borderWidth: isSelected ? 2 : 1,
                                                borderColor: isSelected ? Colors.text.action : Colors.borders.light
                                            }}
                                            onPress={() => {
                                                setContestData(prev => ({
                                                    ...prev,
                                                    selectedMarketIds: isSelected
                                                        ? prev.selectedMarketIds.filter(id => id !== market.router_market_id)
                                                        : [...prev.selectedMarketIds, market.router_market_id]
                                                }));
                                            }}
                                        >
                                            <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                                <View
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: 4,
                                                        borderWidth: 2,
                                                        borderColor: isSelected ? Colors.text.success : Colors.borders.light,
                                                        backgroundColor: isSelected ? Colors.text.success : 'transparent',
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    {isSelected && <Icons.CheckIcon color={Colors.text.white} size={12} />}
                                                </View>
                                                <View transparent style={{ flex: 1 }}>
                                                    <Text theme='h2' size={13}>{market.market_label}</Text>
                                                    <Text theme='description' size={11} style={{ marginTop: 2 }}>
                                                        {market.market_description}
                                                    </Text>
                                                </View>
                                            </View>
                                        </TouchableOpacity>
                                    );
                                })}

                                {/* Partner Selection */}
                                <Text theme='h2' size={14} style={{ marginTop: 20, marginBottom: 10 }}>
                                    Select Partner:
                                </Text>
                                {contestData.partners.map(partner => (
                                    <View
                                        key={partner.partner_id}
                                        transparent
                                        style={{
                                            marginBottom: 10,
                                            padding: 15,
                                            backgroundColor: Colors.views.header,
                                            borderRadius: 8,
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Text theme='h2' size={14}>{partner.name}</Text>
                                        <Button
                                            type='action'
                                            title='Auto-Link'
                                            onPress={async () => {
                                                if (contestData.selectedMarketIds.length === 0) {
                                                    alert('Please select at least one market');
                                                    return;
                                                }

                                                try {
                                                    setContestData(prev => ({ ...prev, loading: true }));
                                                    const results = [];

                                                    // Call auto-link for each selected market
                                                    for (const marketId of contestData.selectedMarketIds) {
                                                        const result = await BetRouterApi.BetRouter.AutoLinkingApi.autoLinkMarkets(
                                                            league.router_league_id,
                                                            marketId,
                                                            partner.partner_id
                                                        );
                                                        results.push(result);
                                                    }

                                                    // Combine results
                                                    const combinedResults = {
                                                        entity_type: 'market',
                                                        partner_name: partner.name,
                                                        total_router_entities: results.reduce((sum, r) => sum + r.total_router_entities, 0),
                                                        matched_count: results.reduce((sum, r) => sum + r.matched_count, 0),
                                                        created_count: results.reduce((sum, r) => sum + (r.created_count || 0), 0),
                                                        updated_count: results.reduce((sum, r) => sum + (r.updated_count || 0), 0),
                                                        failed_count: results.reduce((sum, r) => sum + r.failed_count, 0),
                                                        results: results.flatMap(r => r.results)
                                                    };

                                                    setContestData(prev => ({
                                                        ...prev,
                                                        autoLinkResults: combinedResults,
                                                        showMarketAutoLinkModal: false,
                                                        showAutoLinkResults: true,
                                                        selectedMarketIds: [],
                                                        loading: false
                                                    }));
                                                } catch (error) {
                                                    console.error('Error auto-linking markets:', error);
                                                    alert('Error auto-linking markets: ' + (error as any).message);
                                                    setContestData(prev => ({ ...prev, loading: false }));
                                                }
                                            }}
                                            style={{ paddingHorizontal: 15, paddingVertical: 8 }}
                                        />
                                    </View>
                                ))}
                            </View>
                        </ScrollView>

                        <View type='footer' style={{ padding: 15 }}>
                            <Button
                                type='close'
                                title='Cancel'
                                onPress={() => setContestData(prev => ({
                                    ...prev,
                                    showMarketAutoLinkModal: false,
                                    selectedMarketIds: []
                                }))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Manage Markets Modal */}
            {contestData.showManageMarketsModal && contestData.selectedContest && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ maxWidth: 600, width: '100%', maxHeight: '80%', borderRadius: 8, overflow: 'hidden' }}>
                        <View type='header' style={{ padding: 20 }}>
                            <Text theme='h1' size={18}>Manage Markets</Text>
                            <Text theme='description' size={14} style={{ marginTop: 5 }}>
                                Select which markets are supported for "{contestData.selectedContest.contest_label}"
                            </Text>
                        </View>

                        <ScrollView style={{ flex: 1 }}>
                            <View transparent style={{ padding: 20 }}>
                                {contestData.allRouterMarkets.length === 0 ? (
                                    <Text theme='description' size={13} style={{ textAlign: 'center', padding: 20 }}>
                                        No markets available
                                    </Text>
                                ) : (
                                    contestData.allRouterMarkets.map(market => {
                                        const isSupported = contestData.marketContests.some(mc => mc.router_market_id === market.router_market_id);
                                        return (
                                            <TouchableOpacity
                                                key={market.router_market_id}
                                                style={{
                                                    padding: 15,
                                                    marginBottom: 10,
                                                    backgroundColor: isSupported ? Colors.text.action : Colors.views.header,
                                                    borderRadius: 8,
                                                    borderWidth: isSupported ? 2 : 1,
                                                    borderColor: isSupported ? Colors.text.success : Colors.borders.light
                                                }}
                                                onPress={() => toggleMarketSupport(market.router_market_id)}
                                            >
                                                <View transparent style={{ flexDirection: 'row', alignItems: 'center' }}>
                                                    <View transparent style={{ marginRight: 10 }}>
                                                        {isSupported ? (
                                                            <Icons.CheckCirlceIcon color={Colors.text.success} size={20} />
                                                        ) : (
                                                            <View
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    borderWidth: 2,
                                                                    borderColor: Colors.borders.light,
                                                                    borderRadius: 4
                                                                }}
                                                            />
                                                        )}
                                                    </View>
                                                    <View transparent style={{ flex: 1 }}>
                                                        <Text theme='h2' size={14}>{market.market_label}</Text>
                                                        <Text theme='description' size={12} style={{ marginTop: 3 }}>
                                                            {market.market_description}
                                                        </Text>
                                                        {isSupported && (
                                                            <Text theme='description' size={12} style={{ marginTop: 3, color: Colors.text.success }}>
                                                                ✓ Currently Active
                                                            </Text>
                                                        )}
                                                    </View>
                                                </View>
                                            </TouchableOpacity>
                                        );
                                    })
                                )}
                            </View>
                        </ScrollView>

                        <View type='footer' style={{ padding: 15 }}>
                            <Button
                                type='action'
                                title='Done'
                                onPress={() => setContestData(prev => ({ ...prev, showManageMarketsModal: false }))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Link Markets Modal */}
            {contestData.showLinkMarketsModal && contestData.selectedMarketContest && contestData.selectedPartner && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ width: '90%', maxWidth: 600, maxHeight: '80%', borderRadius: 8, overflow: 'hidden', flexDirection: 'column' }}>
                        <View type='header' style={{ padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <View transparent>
                                <Text theme='h1' size={18}>Link Raw Markets</Text>
                                <Text theme='description' size={12} style={{ marginTop: 4 }}>
                                    {contestData.selectedPartner.name} → {contestData.allRouterMarkets.find(m => m.router_market_id === contestData.selectedMarketContest?.router_market_id)?.market_label}
                                </Text>
                            </View>
                            <TouchableOpacity onPress={() => setContestData(prev => ({ ...prev, showLinkMarketsModal: false }))}>
                                <Icons.CloseIcon size={24} color={Colors.text.h1} />
                            </TouchableOpacity>
                        </View>

                        {/* Already Linked Section */}
                        {contestData.alreadyLinkedRawMarkets.length > 0 && (
                            <View transparent style={{ padding: 16, paddingTop: 0 }}>
                                <Text theme='h2' size={13} style={{ marginBottom: 8 }}>Already Linked:</Text>
                                {contestData.alreadyLinkedRawMarkets.map(raw => (
                                    <View
                                        key={raw.raw_market_id}
                                        transparent
                                        style={{
                                            padding: 8,
                                            marginBottom: 4,
                                            backgroundColor: Colors.views.header,
                                            borderRadius: 4
                                        }}
                                    >
                                        <Text theme='description' size={12}>{raw.pretty_title} ({raw.external_id})</Text>
                                    </View>
                                ))}
                            </View>
                        )}

                        <ScrollView style={{ flex: 1 }}>
                            <View transparent style={{ padding: 16 }}>
                                <Text theme='h2' size={13} style={{ marginBottom: 8 }}>Available Raw Markets:</Text>
                                {contestData.availableRawMarkets.length === 0 ? (
                                    <Text theme='description' size={12} style={{ textAlign: 'center', padding: 20, opacity: 0.6 }}>
                                        No available raw markets to link
                                    </Text>
                                ) : (
                                    contestData.availableRawMarkets.map(rawMarket => (
                                        <View
                                            key={rawMarket.raw_market_id}
                                            transparent
                                            style={{
                                                padding: 12,
                                                marginBottom: 8,
                                                backgroundColor: Colors.views.header,
                                                borderRadius: 6,
                                                borderWidth: 1,
                                                borderColor: Colors.borders.light
                                            }}
                                        >
                                            <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <View transparent style={{ flex: 1 }}>
                                                    <Text theme='h2' size={13}>{rawMarket.pretty_title}</Text>
                                                    <Text theme='description' size={11} style={{ marginTop: 2 }}>
                                                        External ID: {rawMarket.external_id}
                                                    </Text>
                                                </View>
                                                <Button
                                                    type='action'
                                                    title='Link'
                                                    onPress={() => openManualLinkModal(rawMarket)}
                                                    style={{ paddingHorizontal: 12, paddingVertical: 6 }}
                                                />
                                            </View>
                                        </View>
                                    ))
                                )}
                            </View>
                        </ScrollView>

                        <View type='footer' style={{ padding: 16 }}>
                            <Button
                                type='close'
                                title="Close"
                                onPress={() => setContestData(prev => ({ ...prev, showLinkMarketsModal: false }))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Add Variable Modal */}
            {contestData.showAddVariableModal && contestData.selectedMarketContest && contestData.selectedPartner && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ maxWidth: 500, width: '100%', borderRadius: 8, overflow: 'hidden' }}>
                        <View type='header' style={{ padding: 20 }}>
                            <Text theme='h1' size={18}>Add Variable</Text>
                            <Text theme='description' size={14} style={{ marginTop: 5 }}>
                                {contestData.selectedPartner.name} • {contestData.allRouterMarkets.find(m => m.router_market_id === contestData.selectedMarketContest?.router_market_id)?.market_label}
                            </Text>
                        </View>

                        <View type='body' style={{ padding: 20 }}>
                            {(() => {
                                const routerMarket = contestData.allRouterMarkets.find(
                                    m => m.router_market_id === contestData.selectedMarketContest?.router_market_id
                                );
                                const variableType = routerMarket?.default_variable_type || 'spread';

                                // Try to find home participant from existing partner markets
                                const homeSide = contestData.marketSides.find(s => s.router_market_id === routerMarket?.router_market_id && s.side_key === 'home');
                                const existingHomeMarket = contestData.inspectPartnerMarkets.length > 0
                                    ? contestData.inspectPartnerMarkets.find(pm => pm.market_side_id === homeSide?.market_side_id)
                                    : contestData.partnerMarkets.find(pm =>
                                        pm.router_market_id === routerMarket?.router_market_id &&
                                        pm.market_side_id === homeSide?.market_side_id &&
                                        pm.router_contest_id === contestData.selectedContest?.router_contest_id
                                    );
                                const homeParticipantId = existingHomeMarket?.partner_participant_id;

                                return (
                                    <>
                                        <Text theme='description' size={13} style={{ marginBottom: 15 }}>
                                            {variableType === 'spread'
                                                ? 'Enter the spread value from the HOME team\'s perspective. For example, if the home team is favored by 7 points, enter -7. This creates home -7 and away +7.'
                                                : 'Enter the total value (e.g., 220.5 for over/under 220.5). This will automatically create both over and under sides.'}
                                        </Text>

                                        {homeParticipantId && (() => {
                                            const participantName = getParticipantName(homeParticipantId);
                                            return (
                                                <View transparent style={{ padding: 10, backgroundColor: Colors.views.header, borderRadius: 6, marginBottom: 12 }}>
                                                    <Text theme='description' size={11} style={{ marginBottom: 4 }}>Home Team:</Text>
                                                    <Text theme='h2' size={12}>
                                                        {participantName || homeParticipantId}
                                                    </Text>
                                                    {participantName && (
                                                        <Text theme='description' size={10} style={{ marginTop: 2 }}>
                                                            ID: {homeParticipantId}
                                                        </Text>
                                                    )}
                                                </View>
                                            );
                                        })()}

                                        <Text theme='h2' size={13} style={{ marginBottom: 8 }}>
                                            {variableType === 'spread' ? 'Home Team Spread:' : 'Total Value:'}
                                        </Text>

                                        <TextInput
                                            style={{
                                                padding: 12,
                                                borderWidth: 1,
                                                borderColor: Colors.borders.light,
                                                borderRadius: 6,
                                                backgroundColor: Colors.views.header,
                                                color: Colors.text.h1,
                                                fontSize: 14
                                            }}
                                            value={contestData.newVariableValue}
                                            onChangeText={(text) => setContestData(prev => ({ ...prev, newVariableValue: text }))}
                                            placeholder={variableType === 'spread' ? 'e.g., -7 or +3.5' : 'e.g., 220.5'}
                                            placeholderTextColor={Colors.text.description}
                                            keyboardType="numeric"
                                        />

                                        {variableType === 'spread' && contestData.newVariableValue && !isNaN(parseFloat(contestData.newVariableValue)) && (
                                            <Text theme='description' size={11} style={{ marginTop: 8, fontStyle: 'italic' }}>
                                                Will create: Home {contestData.newVariableValue} / Away {parseFloat(contestData.newVariableValue) * -1}
                                            </Text>
                                        )}

                                        <Text theme='description' size={11} style={{ marginTop: 10, fontStyle: 'italic' }}>
                                            This will create partner markets for all raw markets from {contestData.selectedPartner.name} that match this contest.
                                        </Text>
                                    </>
                                );
                            })()}
                        </View>

                        <View type='footer' style={{ padding: 15, flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
                            <Button
                                type='close'
                                title='Cancel'
                                onPress={() => setContestData(prev => ({
                                    ...prev,
                                    showAddVariableModal: false,
                                    newVariableValue: ''
                                }))}
                            />
                            <Button
                                type='action'
                                title='Add Variable'
                                onPress={addVariable}
                                disabled={!contestData.newVariableValue || isNaN(parseFloat(contestData.newVariableValue))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Inspect Partner Markets Modal */}
            {contestData.showInspectModal && contestData.selectedMarketContest && contestData.selectedPartner && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ width: '90%', maxWidth: 800, maxHeight: '85%', borderRadius: 8, overflow: 'hidden', flexDirection: 'column' }}>
                        <View type='header' style={{ padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <View transparent>
                                <Text theme='h1' size={18}>Partner Markets</Text>
                                <Text theme='description' size={12} style={{ marginTop: 4 }}>
                                    {contestData.selectedPartner.name} • {contestData.allRouterMarkets.find(m => m.router_market_id === contestData.selectedMarketContest?.router_market_id)?.market_label}
                                </Text>
                            </View>
                            <TouchableOpacity onPress={() => setContestData(prev => ({ ...prev, showInspectModal: false }))}>
                                <Icons.CloseIcon size={24} color={Colors.text.h1} />
                            </TouchableOpacity>
                        </View>

                        <ScrollView style={{ flex: 1 }}>
                            <View transparent style={{ padding: 16 }}>
                                {contestData.inspectPartnerMarkets.length === 0 ? (
                                    <Text theme='description' size={13} style={{ textAlign: 'center', padding: 20 }}>
                                        No partner markets found
                                    </Text>
                                ) : (
                                    <>
                                        <Text theme='h2' size={14} style={{ marginBottom: 12 }}>
                                            {contestData.inspectPartnerMarkets.length} Partner Market{contestData.inspectPartnerMarkets.length !== 1 ? 's' : ''}
                                        </Text>
                                        {contestData.inspectPartnerMarkets.map((pm, index) => {
                                            // Find enriched data
                                            const side = contestData.marketSides.find(s => s.market_side_id === pm.market_side_id);
                                            const variable = contestData.marketVariables.find(v => v.router_market_variable_id === pm.router_market_variable_id);

                                            return (
                                                <View
                                                    key={pm.partner_market_id}
                                                    transparent
                                                    style={{
                                                        padding: 14,
                                                        marginBottom: 10,
                                                        backgroundColor: Colors.views.header,
                                                        borderRadius: 8,
                                                        borderWidth: 1,
                                                        borderColor: Colors.borders.light
                                                    }}
                                                >
                                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                                                        <Text theme='h2' size={13}>
                                                            {side ? `${side.side_key}` : `Partner Market #${index + 1}`}
                                                            {variable ? ` (${variable.variable > 0 ? '+' : ''}${variable.variable})` : ''}
                                                        </Text>
                                                        <Text theme='description' size={11}>
                                                            ID: {pm.partner_market_id.slice(0, 8)}...
                                                        </Text>
                                                    </View>

                                                    <View transparent style={{ gap: 6 }}>
                                                        <View transparent style={{ flexDirection: 'row' }}>
                                                            <Text theme='description' size={12} style={{ width: 160 }}>External ID:</Text>
                                                            <Text theme='h2' size={12} style={{ flex: 1 }}>{pm.external_id}</Text>
                                                        </View>

                                                        {side && (
                                                            <View transparent style={{ flexDirection: 'row' }}>
                                                                <Text theme='description' size={12} style={{ width: 160 }}>Side:</Text>
                                                                <Text theme='h2' size={12} style={{ flex: 1 }}>
                                                                    {side.side_key} ({side.side_type})
                                                                </Text>
                                                            </View>
                                                        )}

                                                        {variable && (
                                                            <View transparent style={{ flexDirection: 'row' }}>
                                                                <Text theme='description' size={12} style={{ width: 160 }}>Variable:</Text>
                                                                <Text theme='h2' size={12} style={{ flex: 1 }}>
                                                                    {variable.variable} ({variable.variable_type})
                                                                </Text>
                                                            </View>
                                                        )}

                                                        <View transparent style={{ flexDirection: 'row' }}>
                                                            <Text theme='description' size={12} style={{ width: 160 }}>Raw Market ID:</Text>
                                                            <Text theme='h2' size={12} style={{ flex: 1 }}>{pm.raw_market_id}</Text>
                                                        </View>

                                                        {pm.partner_participant_id && (() => {
                                                            const participantName = getParticipantName(pm.partner_participant_id);
                                                            return (
                                                                <View transparent style={{ flexDirection: 'row' }}>
                                                                    <Text theme='description' size={12} style={{ width: 160 }}>Participant:</Text>
                                                                    <View transparent style={{ flex: 1 }}>
                                                                        <Text theme='h2' size={12}>
                                                                            {participantName || pm.partner_participant_id}
                                                                        </Text>
                                                                        {participantName && (
                                                                            <Text theme='description' size={10} style={{ marginTop: 2 }}>
                                                                                ID: {pm.partner_participant_id}
                                                                            </Text>
                                                                        )}
                                                                    </View>
                                                                </View>
                                                            );
                                                        })()}

                                                        {pm.timeframe_id && (
                                                            <View transparent style={{ flexDirection: 'row' }}>
                                                                <Text theme='description' size={12} style={{ width: 160 }}>Timeframe ID:</Text>
                                                                <Text theme='h2' size={12} style={{ flex: 1 }}>{pm.timeframe_id}</Text>
                                                            </View>
                                                        )}

                                                        {pm.price_key && (
                                                            <View transparent style={{ flexDirection: 'row' }}>
                                                                <Text theme='description' size={12} style={{ width: 160 }}>Price Key:</Text>
                                                                <Text theme='h2' size={12} style={{ flex: 1 }}>{pm.price_key}</Text>
                                                            </View>
                                                        )}
                                                    </View>
                                                </View>
                                            );
                                        })}
                                    </>
                                )}
                            </View>
                        </ScrollView>

                        <View type='footer' style={{ padding: 16 }}>
                            <Button
                                type='close'
                                title="Close"
                                onPress={() => setContestData(prev => ({ ...prev, showInspectModal: false }))}
                            />
                        </View>
                    </View>
                </View>
            )}

            {/* Manual Link Modal */}
            {contestData.showManualLinkModal && contestData.selectedRawMarket && contestData.linkingData && (
                <View
                    style={{
                        position: 'fixed' as any,
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: Colors.views.blur,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 20,
                        zIndex: 1000
                    }}
                >
                    <View float style={{ width: '90%', maxWidth: 600, maxHeight: '85%', borderRadius: 8, overflow: 'hidden', flexDirection: 'column' }}>
                        {/* Header */}
                        <View type='header' style={{ padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <View transparent>
                                <Text theme='h1' size={18}>Link Market</Text>
                                <Text theme='description' size={12} style={{ marginTop: 4 }}>
                                    {contestData.selectedRawMarket.pretty_title}
                                </Text>
                            </View>
                            <TouchableOpacity onPress={() => setContestData(prev => ({ ...prev, showManualLinkModal: false }))}>
                                <Icons.CloseIcon size={24} color={Colors.text.h1} />
                            </TouchableOpacity>
                        </View>

                        {contestData.loadingModal ? (
                            <View transparent style={{ padding: 40, alignItems: 'center' }}>
                                <ActivityIndicator size="large" color={Colors.text.h1} />
                            </View>
                        ) : (
                            <>
                                <ScrollView style={{ flex: 1 }}>
                                    <View transparent style={{ padding: 16 }}>
                                        {(() => {
                                            const routerMarket = contestData.allRouterMarkets.find(
                                                m => m.router_market_id === contestData.selectedMarketContest?.router_market_id
                                            );

                                            return (
                                                <>
                                                    {/* Variable Input (if required) - at the top */}
                                                    {routerMarket?.variable_required && (
                                                        <View transparent style={{ marginBottom: 20 }}>
                                                            <Text theme='h2' size={13} style={{ marginBottom: 8 }}>
                                                                {routerMarket.default_variable_type === 'spread' ? 'Spread' : 'Total'} Value: <Text theme='error' size={13}>*</Text>
                                                            </Text>
                                                            <TextInput
                                                                value={contestData.manualLinkForm.variable}
                                                                onChangeText={(text) => setContestData(prev => ({
                                                                    ...prev,
                                                                    manualLinkForm: { ...prev.manualLinkForm, variable: text }
                                                                }))}
                                                                placeholder={routerMarket.default_variable_type === 'spread' ? 'e.g., -7 or +3.5' : 'e.g., 220.5'}
                                                                keyboardType="numeric"
                                                                style={{
                                                                    padding: 12,
                                                                    borderWidth: 1,
                                                                    borderColor: Colors.borders.light,
                                                                    borderRadius: 6,
                                                                    backgroundColor: Colors.views.header,
                                                                    color: Colors.text.h1,
                                                                    fontSize: 14
                                                                }}
                                                            />
                                                        </View>
                                                    )}

                                                    {/* Side Toggle */}
                                                    <View transparent style={{ marginBottom: 20 }}>
                                                        <Text theme='h2' size={13} style={{ marginBottom: 8 }}>
                                                            Select Side: <Text theme='error' size={13}>*</Text>
                                                        </Text>
                                                        <View transparent style={{ flexDirection: 'row', gap: 8 }}>
                                                            {contestData.linkingData.market_sides.map((side, index) => {
                                                                const sideParticipant = contestData.manualLinkForm.sideParticipants[index];
                                                                const hasParticipant = !!sideParticipant?.partner_participant_id;
                                                                const isSelected = contestData.selectedSideIndex === index;

                                                                return (
                                                                    <TouchableOpacity
                                                                        key={side.market_side_id}
                                                                        onPress={() => setContestData(prev => ({
                                                                            ...prev,
                                                                            selectedSideIndex: index,
                                                                            participantSearchValue: '',
                                                                            participantPage: 0
                                                                        }))}
                                                                        style={{
                                                                            flex: 1,
                                                                            padding: 12,
                                                                            backgroundColor: Colors.views.header,
                                                                            borderRadius: 6,
                                                                            borderWidth: 2,
                                                                            borderColor: isSelected ? Colors.text.success : Colors.borders.light,
                                                                            flexDirection: 'row',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: 6
                                                                        }}
                                                                    >
                                                                        <Text size={14} style={{
                                                                            color: isSelected ? Colors.text.success : Colors.text.h1,
                                                                            fontWeight: isSelected ? '600' : '400'
                                                                        }}>
                                                                            {side.side_key}
                                                                        </Text>
                                                                        {hasParticipant && (
                                                                            <Text size={14} style={{ color: Colors.text.success }}>✓</Text>
                                                                        )}
                                                                    </TouchableOpacity>
                                                                );
                                                            })}
                                                        </View>
                                                    </View>

                                                    {/* Participant Selection for Selected Side */}
                                                    {(() => {
                                                        const selectedSide = contestData.linkingData.market_sides[contestData.selectedSideIndex];
                                                        const sideParticipant = contestData.manualLinkForm.sideParticipants[contestData.selectedSideIndex];
                                                        const contestLeagueId = contestData.selectedContest?.router_league_id;

                                                        // Filter and paginate participants
                                                        const searchFiltered = contestData.linkingData.participants.filter(p => {
                                                            const searchLower = contestData.participantSearchValue.toLowerCase();
                                                            return p.name.toLowerCase().includes(searchLower) ||
                                                                   p.raw_name.toLowerCase().includes(searchLower) ||
                                                                   p.external_id.toLowerCase().includes(searchLower);
                                                        });

                                                        const leagueFiltered = searchFiltered.filter(p => {
                                                            const routerParticipant = contestData.participants.find(
                                                                rp => rp.participant_id === p.router_participant_id
                                                            );
                                                            return routerParticipant?.router_league_id === contestLeagueId;
                                                        });

                                                        const totalPages = Math.ceil(leagueFiltered.length / PARTICIPANTS_PER_PAGE);
                                                        const startIdx = contestData.participantPage * PARTICIPANTS_PER_PAGE;
                                                        const endIdx = startIdx + PARTICIPANTS_PER_PAGE;
                                                        const paginatedParticipants = leagueFiltered.slice(startIdx, endIdx);

                                                        return (
                                                            <View transparent style={{ marginBottom: 20 }}>
                                                                <Text theme='h2' size={13} style={{ marginBottom: 8 }}>
                                                                    Participant for {selectedSide?.side_key}: <Text theme='error' size={13}>*</Text>
                                                                </Text>
                                                                <Text theme='description' size={11} style={{ marginBottom: 12 }}>
                                                                    {leagueFiltered.length} participants available
                                                                </Text>

                                                                {/* Search Box */}
                                                                <View transparent style={{ marginBottom: 12 }}>
                                                                    <SearchBox
                                                                        placeholder="Search participants..."
                                                                        onChange={(text) => setContestData(prev => ({
                                                                            ...prev,
                                                                            participantSearchValue: text,
                                                                            participantPage: 0
                                                                        }))}
                                                                        hide_search_button={true}
                                                                    />
                                                                </View>

                                                                {/* Participants List */}
                                                                {paginatedParticipants.length === 0 ? (
                                                                    <Text theme='description' size={12} style={{ textAlign: 'center', padding: 20, opacity: 0.6 }}>
                                                                        {contestData.participantSearchValue
                                                                            ? 'No participants match your search'
                                                                            : 'No participants available for this league'}
                                                                    </Text>
                                                                ) : (
                                                                    paginatedParticipants.map(participant => {
                                                                        const isSelected = sideParticipant?.partner_participant_id === participant.partner_participant_id;
                                                                        return (
                                                                            <TouchableOpacity
                                                                                key={participant.partner_participant_id}
                                                                                onPress={() => {
                                                                                    setContestData(prev => ({
                                                                                        ...prev,
                                                                                        manualLinkForm: {
                                                                                            ...prev.manualLinkForm,
                                                                                            sideParticipants: prev.manualLinkForm.sideParticipants.map((sp, idx) =>
                                                                                                idx === prev.selectedSideIndex
                                                                                                    ? { ...sp, partner_participant_id: participant.partner_participant_id }
                                                                                                    : sp
                                                                                            )
                                                                                        }
                                                                                    }));
                                                                                }}
                                                                                style={{
                                                                                    padding: 12,
                                                                                    marginBottom: 6,
                                                                                    backgroundColor: Colors.views.header,
                                                                                    borderRadius: 6,
                                                                                    borderWidth: 1,
                                                                                    borderColor: isSelected ? Colors.text.success : Colors.borders.light
                                                                                }}
                                                                            >
                                                                                <Text size={13} style={{ color: isSelected ? Colors.text.success : Colors.text.h1, fontWeight: isSelected ? '600' : '400' }}>
                                                                                    {participant.name}
                                                                                </Text>
                                                                                <Text theme='description' size={11} style={{ marginTop: 2 }}>
                                                                                    {participant.raw_name} • External ID: {participant.external_id}
                                                                                </Text>
                                                                            </TouchableOpacity>
                                                                        );
                                                                    })
                                                                )}

                                                                {/* Pagination */}
                                                                {totalPages > 1 && (
                                                                    <View transparent style={{ marginTop: 12 }}>
                                                                        <Pagination
                                                                            offset={contestData.participantPage}
                                                                            pages={totalPages}
                                                                            onSelectPage={(page) => setContestData(prev => ({
                                                                                ...prev,
                                                                                participantPage: page
                                                                            }))}
                                                                        />
                                                                    </View>
                                                                )}
                                                            </View>
                                                        );
                                                    })()}

                                                    {/* Timeframe Selection (if available) */}
                                                    {contestData.linkingData.timeframes.length > 0 && (
                                                        <View transparent style={{ marginBottom: 20 }}>
                                                            <Text theme='h2' size={13} style={{ marginBottom: 8 }}>
                                                                Timeframe:
                                                            </Text>
                                                            {contestData.linkingData.timeframes.map(timeframe => {
                                                                const isSelected = contestData.manualLinkForm.timeframe_id === timeframe.timeframe_id;
                                                                return (
                                                                    <TouchableOpacity
                                                                        key={timeframe.timeframe_id}
                                                                        onPress={() => setContestData(prev => ({
                                                                            ...prev,
                                                                            manualLinkForm: { ...prev.manualLinkForm, timeframe_id: timeframe.timeframe_id }
                                                                        }))}
                                                                        style={{
                                                                            padding: 12,
                                                                            marginBottom: 6,
                                                                            backgroundColor: Colors.views.header,
                                                                            borderRadius: 6,
                                                                            borderWidth: 1,
                                                                            borderColor: isSelected ? Colors.text.success : Colors.borders.light
                                                                        }}
                                                                    >
                                                                        <Text size={13} style={{ color: isSelected ? Colors.text.success : Colors.text.h1, fontWeight: isSelected ? '600' : '400' }}>
                                                                            {timeframe.label}
                                                                        </Text>
                                                                    </TouchableOpacity>
                                                                );
                                                            })}
                                                        </View>
                                                    )}

                                                    {/* Price Key (optional, for specific partners like Kalshi) - Per Side */}
                                                    {(() => {
                                                        const selectedSide = contestData.linkingData.market_sides[contestData.selectedSideIndex];
                                                        const sideParticipant = contestData.manualLinkForm.sideParticipants[contestData.selectedSideIndex];

                                                        return (
                                                            <View transparent style={{ marginBottom: 20 }}>
                                                                <Text theme='h2' size={13} style={{ marginBottom: 8 }}>
                                                                    Price Key for {selectedSide?.side_key} (optional):
                                                                </Text>
                                                                <TextInput
                                                                    value={sideParticipant?.price_key || ''}
                                                                    onChangeText={(text) => setContestData(prev => ({
                                                                        ...prev,
                                                                        manualLinkForm: {
                                                                            ...prev.manualLinkForm,
                                                                            sideParticipants: prev.manualLinkForm.sideParticipants.map((sp, idx) =>
                                                                                idx === prev.selectedSideIndex
                                                                                    ? { ...sp, price_key: text }
                                                                                    : sp
                                                                            )
                                                                        }
                                                                    }))}
                                                                    placeholder="e.g., yes or no (for Kalshi)"
                                                                    style={{
                                                                        padding: 12,
                                                                        borderWidth: 1,
                                                                        borderColor: Colors.borders.light,
                                                                        borderRadius: 6,
                                                                        backgroundColor: Colors.views.header,
                                                                        color: Colors.text.h1,
                                                                        fontSize: 14
                                                                    }}
                                                                />
                                                                <Text theme='description' size={11} style={{ marginTop: 4 }}>
                                                                    Used by some partners to indicate which price to use for this side
                                                                </Text>
                                                            </View>
                                                        );
                                                    })()}
                                                </>
                                            );
                                        })()}
                                    </View>
                                </ScrollView>

                                {/* Footer */}
                                <View type='footer' style={{ padding: 16, flexDirection: 'row', gap: 12 }}>
                                    <Button
                                        type='close'
                                        title="Cancel"
                                        onPress={() => setContestData(prev => ({ ...prev, showManualLinkModal: false }))}
                                        style={{ flex: 1 }}
                                    />
                                    <Button
                                        type='action'
                                        title="Link Market"
                                        onPress={submitManualLink}
                                        style={{ flex: 1 }}
                                    />
                                </View>
                            </>
                        )}
                    </View>
                </View>
            )}
        </>
    );
};

export default LeagueContests;
